import { CheckOutlined, LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Input, message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceConfirmWriteOffEndpoint,
  useStocksServiceGetWriteOffEndpoint,
  useStocksServiceGetWriteOffEndpointKey,
  useStocksServiceGetWriteOffListEndpointKey,
  useStocksServiceRestoreWriteOffEndpoint,
  useStocksServiceSendWriteOffEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InventoryDocumentState,
  Permissions,
  WriteOffApprovalStatus
} from 'src/api/requests'
import {
  ConfirmModal,
  LoadingScreen,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { SettlementsConfirmedDetails } from 'src/features/stock/components'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

enum Actions {
  SEND = 'send',
  CONFIRM = 'confirm',
  REJECT = 'reject',
  RESTORE = 'restore'
}

export const StockSettlementsDetailsPage = () => {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [modalAction, setModalAction] = useState<null | Actions>(null)
  const isModalOpen = modalAction !== null

  const [form] = Form.useForm()

  const navigate = useNavigate()
  const { t, currentLang } = useLocales()
  const { hasPermission } = useUser()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Write_OFF_${id}`
  })

  const { data, isLoading } = useStocksServiceGetWriteOffEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const invalidateWriteOffQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetWriteOffEndpointKey, String(id)]
    })

    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetWriteOffListEndpointKey]
    })
  }

  const { mutate: send } = useStocksServiceSendWriteOffEndpoint({
    onSuccess: () => {
      invalidateWriteOffQueries()
      message.success(t('stock_applications.settlement_sent'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: confirmWriteOff } = useStocksServiceConfirmWriteOffEndpoint({
    onSuccess: () => {
      invalidateWriteOffQueries()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: restoreWriteOff } = useStocksServiceRestoreWriteOffEndpoint({
    onSuccess: () => {
      invalidateWriteOffQueries()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.settlements.root)
  }

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      form.setFieldsValue({
        documentDate: toDayjs(data?.documentDate),
        documentNumber: data?.documentNumber,
        comment: data?.comment,
        reasons: data?.reasons?.map(
          (reason) => reason?.translation?.[currentLang?.value]
        ),
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.internationalName?.name} ${item?.doses}`,
          productName: item?.product?.name,
          producerName: item?.producer?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          unitPrice: item?.unitPrice,
          priceTotal: (item?.quantity ?? 0) * (item?.unitPrice ?? 0),
          quantity: `${item?.quantity}`
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, id])

  const onSend = (comment?: string) => {
    send({ id: String(id), requestBody: { comment } })
  }

  const onConfirm = (status: WriteOffApprovalStatus, comment?: string) => {
    confirmWriteOff({
      id: String(id),
      requestBody: {
        approvalStatus: status,
        comment
      }
    })
  }

  const onRestore = (comment?: string) => {
    restoreWriteOff({ id: String(id), requestBody: { comment } })
  }

  const onOk = async () => {
    const { comment } = await form.validateFields()

    const actionMap = {
      [Actions.SEND]: () => onSend(comment),
      [Actions.CONFIRM]: () =>
        onConfirm(WriteOffApprovalStatus.ACCEPT, comment),
      [Actions.REJECT]: () => onConfirm(WriteOffApprovalStatus.REJECT, comment),
      [Actions.RESTORE]: () => onRestore(comment)
    }

    if (modalAction) {
      actionMap[modalAction]()
    }

    setModalAction(null)
  }

  const canSend = hasPermission(Permissions.STOCKS_STOCK_SEND_WRITE_OFFS)
  const canConfirm = hasPermission(Permissions.STOCKS_STOCK_CONFIRM_WRITE_OFFS)
  const canRestore = hasPermission(Permissions.STOCKS_STOCK_CANCEL_WRITE_OFFS)

  const isWaiting = data?.state === InventoryDocumentState.WAITING
  const isNew = data?.state === InventoryDocumentState.NEW
  const isAccepted = data?.state === InventoryDocumentState.ACCEPTED

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock_write_offs.write_off')}: ${String(data?.code)}`}
      items={[
        {
          title: t('menu.consumable_medicaments'),
          path: paths.settlements.root
        },
        {
          title: t('stock.settlement'),
          path: paths.settlements.root
        },
        {
          title: `${t('stock_write_offs.write_off')}: ${String(data?.code)}`
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Flex gap={8}>
            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            {canSend && isNew && (
              <SaveConfirm
                title={`${t('stock_applications.send')}?`}
                description={t('stock_applications.confirm_send_desc')}
                placement="leftBottom"
                onConfirm={() => setModalAction(Actions.SEND)}
              >
                <Button type="primary">{t('stock_applications.send')}</Button>
              </SaveConfirm>
            )}

            {canConfirm && isWaiting && (
              <>
                <SaveConfirm
                  title={`${t('common.confirm')}?`}
                  placement="leftBottom"
                  onConfirm={() => setModalAction(Actions.CONFIRM)}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    {t('common.confirm')}
                  </Button>
                </SaveConfirm>

                <SaveConfirm
                  title={`${t('stock_administration.reject')}?`}
                  placement="leftBottom"
                  onConfirm={() => setModalAction(Actions.REJECT)}
                >
                  <Button danger>{t('stock_administration.reject')}</Button>
                </SaveConfirm>
              </>
            )}

            {canRestore && isAccepted && (
              <SaveConfirm
                title={t('stock_administration.restore')}
                placement="leftBottom"
                onConfirm={() => setModalAction(Actions.RESTORE)}
              >
                <Button danger>{t('stock_administration.restore')}</Button>
              </SaveConfirm>
            )}
          </Flex>
        </Flex>

        <div ref={printableRef}>
          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            disabled={!!data}
          >
            <SettlementsConfirmedDetails item={data} form={form} />
          </Form>
        </div>
      </Flex>

      <ConfirmModal
        open={isModalOpen}
        onOk={onOk}
        onCancel={() => setModalAction(null)}
        title={t('stock_administration.' + modalAction) + '?'}
        okButtonProps={{ danger: modalAction === Actions.REJECT }}
      >
        <Card>
          <Form form={form} layout="vertical">
            <Form.Item label={t('common.comment')} name="comment">
              <Input.TextArea
                rows={5}
                placeholder={t('common.leave_comment')}
              />
            </Form.Item>
          </Form>
        </Card>
      </ConfirmModal>
    </PageBreadcrumbs>
  )
}
